/**
 * DetoxStoryCard.jsx — iter660 (Phase 4)
 *
 * "Tell me my Detox Story" — генерира AI-обяснение от Claude Haiku, базирано
 * на DUTCH + DNA профила на пациента. 24h кеш, regenerate при нов upload.
 *
 * Educational only. Strict Bulgarian copy.
 */
import React, { useState } from "react";
import axios from "axios";
import { Sparkles, Loader2, AlertCircle, RefreshCcw, BookOpen } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const TX = {
  bg: {
    title: "📖 Разкажи ми моята детокс история",
    sub:   "AI-обяснение на ~2 минути: какво показват именно ВАШИТЕ DUTCH + ДНК данни — на разбираем език с конкретни стъпки",
    cta:   "Генерирай моята история",
    regen: "Регенерирай",
    loading: "Анализираме Вашия профил с AI…",
    cached:  "Кеширано",
    privacy: "Образователно — не заменя лекарска консултация.",
  },
  en: {
    title: "📖 Tell me my Detox Story",
    sub:   "A ~2-min AI explanation of YOUR DUTCH + DNA data in plain language with 3 concrete actions",
    cta:   "Generate my story",
    regen: "Regenerate",
    loading: "AI is analysing your profile…",
    cached:  "Cached",
    privacy: "Educational only — does not replace medical advice.",
  },
  es: {
    title: "📖 Cuéntame mi historia de detox",
    sub:   "Explicación IA de ~2 min de SUS datos DUTCH + ADN con 3 pasos concretos",
    cta:   "Generar mi historia",
    regen: "Regenerar",
    loading: "La IA analiza su perfil…",
    cached:  "En caché",
    privacy: "Sólo educativo.",
  },
};

export default function DetoxStoryCard({ lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [story, setStory] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/bhi/specialist-lab/detox-story`, {});
      setStory(data.story);
      setMeta({ cached: data.cached, generated_at: data.generated_at });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const cardCls = `rounded-2xl border ${isDark ? "bg-[hsl(222,47%,11%)] border-purple-500/30" : "bg-white border-purple-200"}`;

  return (
    <div className={`${cardCls} p-5 mt-3`} data-testid="detox-story-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
          <BookOpen className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="text-base font-extrabold">{t.title}</h4>
          <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{t.sub}</p>
        </div>
      </div>

      {!story && !loading && !err && (
        <button
          onClick={generate}
          className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 flex items-center justify-center gap-2"
          data-testid="detox-story-generate"
        >
          <Sparkles className="w-4 h-4" /> {t.cta}
        </button>
      )}

      {loading && (
        <div className="text-center py-6" data-testid="detox-story-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-500" />
          <p className="text-xs mt-2 opacity-70">{t.loading}</p>
        </div>
      )}

      {err && (
        <div className="p-3 rounded-xl bg-red-500/10 text-red-500 flex items-start gap-2 text-sm" data-testid="detox-story-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div>{err}</div>
            <button
              onClick={generate}
              className="mt-2 px-3 py-1 rounded-lg text-xs font-semibold bg-red-500/20 hover:bg-red-500/30"
            >
              {t.regen}
            </button>
          </div>
        </div>
      )}

      {story && !loading && (
        <div className="space-y-3" data-testid="detox-story-content">
          <div className={`p-4 rounded-xl ${isDark ? "bg-white/[0.03]" : "bg-purple-50/50"}`}>
            {story.split(/\n\n+/).map((para, i) => (
              <p key={i} className={`text-sm leading-relaxed mb-3 last:mb-0 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                {para}
              </p>
            ))}
          </div>
          <div className="flex items-center justify-between text-[11px] opacity-60">
            <span>{meta?.cached ? `✓ ${t.cached}` : "✨ Fresh"} {meta?.generated_at ? `· ${new Date(meta.generated_at).toLocaleString()}` : ""}</span>
            <button
              onClick={generate}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
              data-testid="detox-story-regenerate"
            >
              <RefreshCcw className="w-3 h-3" /> {t.regen}
            </button>
          </div>
          <p className="text-[10px] opacity-50 italic">{t.privacy}</p>
        </div>
      )}
    </div>
  );
}
